import type { Color } from '@xox/shared'
import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import type { GameInfo } from '../lib/games'
import { GameLogo } from './art/GameLogo'
import { arcadeClass } from './ui'

interface Props {
  game: GameInfo
  /** Live, personal line under the chips (progress, boss record…). */
  hook: ReactNode
  /** Viewer's profile color, dressed onto the ice skater. */
  color?: Color
}

const SKIN: Record<
  GameInfo['to'],
  { bg: string; glow: string; ring: string; tone: 'boss' | 'go' | 'drop' }
> = {
  '/xox': {
    bg: 'bg-[linear-gradient(135deg,var(--poster-xox-1),var(--poster-xox-2))]',
    glow: 'bg-[var(--robot-eye)]',
    ring: 'ring-violet-500/40',
    tone: 'boss',
  },
  '/c4': {
    bg: 'bg-[linear-gradient(135deg,var(--poster-c4-1),var(--poster-c4-2))]',
    glow: 'bg-[var(--c4-yellow)]',
    ring: 'ring-amber-300/50',
    tone: 'drop',
  },
  '/cycles': {
    bg: 'bg-[linear-gradient(135deg,var(--poster-cycles-1),var(--poster-cycles-2))]',
    glow: 'bg-[var(--shirt-sky)]',
    ring: 'ring-cyan-300/40',
    tone: 'go',
  },
  '/dungeon': {
    bg: 'bg-[linear-gradient(135deg,var(--poster-dungeon-1),var(--poster-dungeon-2))]',
    glow: 'bg-[var(--dg-torch)]',
    ring: 'ring-amber-400/40',
    tone: 'go',
  },
  '/ice': {
    bg: 'bg-[linear-gradient(135deg,var(--poster-ice-1),var(--poster-ice-2))]',
    glow: 'bg-white',
    ring: 'ring-sky-300/50',
    tone: 'go',
  },
}

/** A full-width painted card for one game; the whole card is the link, "Play" is its slab. */
export function GamePoster({ game, hook, color }: Props) {
  const skin = SKIN[game.to]
  return (
    <Link
      to={game.to}
      className={`group relative flex h-full flex-col gap-4 overflow-hidden rounded-3xl p-5 text-white shadow-lg ring-1 transition-transform hover:-translate-y-0.5 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 ${skin.bg} ${skin.ring}`}
    >
      <div
        className={`pointer-events-none absolute -top-14 -right-14 size-48 rounded-full opacity-25 blur-2xl ${skin.glow}`}
        aria-hidden
      />
      <div className="flex items-center gap-4">
        <GameLogo game={game.to} color={color} className="w-24 animate-float sm:w-28" />
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-black leading-tight tracking-tight">{game.title}</h2>
          <p className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.14em] opacity-80">
            {game.tagline}
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {game.chips.map((chip) => (
              <li
                key={chip}
                className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold ring-1 ring-white/20"
              >
                {chip}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-auto text-sm opacity-90">{hook}</div>
      <span className={`${arcadeClass(skin.tone)} w-full group-hover:brightness-110`}>
        Play <ArrowRight className="size-5" aria-hidden />
      </span>
    </Link>
  )
}
